import React, {
  forwardRef,
  Fragment,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Portal,
} from "@headlessui/react";
import classNames from "classnames";
import {
  cx,
  useSearchInput,
  useTranslations,
  assignRefs,
  useAppVariant,
} from "@jugl-web/utils";
import { useFloating, flip, shift, autoUpdate } from "@floating-ui/react";
import parsePhoneNumber, { CountryCode } from "libphonenumber-js";
import countries from "countries-phone-masks";
import { VList } from "virtua";
import { MobileDrawer } from "../../overlay/MobileDrawer";
import { PlainButton } from "../../primitives/PlainButton";
import { SearchInput } from "../SearchInput";
import type { Country, PhoneInputValue } from "./types";
import { ReactComponent as ArrowIcon } from "./arrow-bottom.svg";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { getUserISO } from "./utils/getUserISO";
import { customerPhoneStringToInputValue } from "./utils/customerPhoneStringToInputValue";

const countriesMasks = countries as unknown as Country[];

const userCountry = countriesMasks.find((item) => getUserISO() === item.iso);

export const validatePhoneNumber = (value: string) => {
  if (!value) {
    return false;
  }

  // Parse the phone string (handles all formats: "911234567890", "IN,+91,1234567890", "+911234567890")
  const { iso, code, phone: number } = customerPhoneStringToInputValue(value);

  if (!iso || !code || !number) {
    return false;
  }

  const country = countriesMasks.find((item) => item.iso === iso);
  if (!country) {
    return false;
  }
  const masks = Array.isArray(country?.mask) ? country?.mask : [country?.mask];
  const parsedCountryMasks = masks.map(
    (mask) => `${country.code}${mask.replace(/[^#0-9]/g, "")}`.length
  );
  const enteredPhoneNumber = `${code}${number?.replace(/[^#0-9]/g, "")}`;
  return parsedCountryMasks.includes(enteredPhoneNumber.length);
};

export const PhoneInput = forwardRef<
  HTMLInputElement,
  {
    value?: { phone: string; code: string; iso: string };
    placeholder?: string;
    className?: string;
    inputClassName?: string;
    listButtonClassName?: string;
    wrapperClassName?: string;
    hideVerticalLine?: boolean;
    maxLength?: number;
    blockNotAllowedCharacters?: boolean;
    onChange: (value: PhoneInputValue) => void;
    onBlur?: () => void;
    onFocus?: () => void;
  }
>(
  (
    {
      value,
      className,
      inputClassName,
      listButtonClassName,
      wrapperClassName,
      hideVerticalLine,
      maxLength,
      blockNotAllowedCharacters,
      onChange,
      onBlur,
      onFocus,
      placeholder,
    },
    ref
  ) => {
    const [internalCountry, setInternalCountry] = useState<{
      iso: string;
      code: string;
    }>({ iso: userCountry?.iso || "IN", code: userCountry?.code || "+91" });

    const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

    if (!value) {
      value = {
        phone: "",
        code: internalCountry.code,
        iso: internalCountry.iso,
      };
    }
    const { isMobile } = useAppVariant();
    const listboxButtonRef = useRef<HTMLButtonElement>(null);
    const listboxOptionsRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const { searchQuery, inputProps, reset } = useSearchInput();
    const { t } = useTranslations();

    const { refs, floatingStyles } = useFloating({
      middleware: [flip({ crossAxis: true }), shift({ crossAxis: true })],
      whileElementsMounted: autoUpdate,
      placement: "right",
    });

    const handleCountrySelect = (e: string) => {
      const [iso, code] = e.split("-");
      const phoneNumber = parsePhoneNumber(
        `${value.phone}`,
        iso as CountryCode
      );

      setInternalCountry({ iso, code });
      setIsMobileDrawerOpen(false);
      onChange({
        ...value,
        isValid:
          /000\d\d\d\d000/g.test(value.phone) ||
          phoneNumber?.isValid() ||
          false,
        code,
        iso,
      });
    };
    const handlePhoneChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const phone = e.target.value.includes("_")
          ? e.target.value
          : e.target.value.replace(/\D+/g, "");
        const phoneNumber = parsePhoneNumber(
          `${value.code}${phone}`,
          value.iso as CountryCode
        );
        let isValid = /000\d\d\d\d000/g.test(phone) || phoneNumber?.isValid();
        if (value.iso === "IN" && phone.length !== 10) {
          isValid = false;
        }
        onChange({
          ...value,
          isValid,
          phone,
        });
      },
      [onChange, value]
    );
    const preparedCountries: Country[] = useMemo(() => {
      const newArray = [...countriesMasks];

      let inItem: Country | undefined;
      let usItem: Country | undefined;
      let myItem: Country | undefined;

      const filteredArray = newArray.filter((item) => {
        if (item.iso === "IN") {
          inItem = item;
          return false;
        }
        if (item.iso === "US") {
          usItem = item;
          return false;
        }
        if (item.iso === "MY") {
          myItem = item;
          return false;
        }
        return true;
      });

      if (inItem) filteredArray.unshift(inItem); // India first
      if (usItem) filteredArray.unshift(usItem); // US second (pushes India to 2nd position)

      if (myItem) {
        filteredArray.splice(2, 0, myItem);
      }

      return filteredArray;
    }, []);

    const searchedCountries = useMemo(
      () =>
        preparedCountries.filter(({ name, code, iso }) =>
          `${name} ${iso} ${code}`
            .toLocaleLowerCase()
            .includes(searchQuery.toLowerCase())
        ),
      [preparedCountries, searchQuery]
    );

    const handleSelect = () => {
      setTimeout(() => {
        if (inputRef?.current) {
          inputRef.current.focus();
          reset();
        }
      }, 0);
    };

    // Headless UI Listbox only toggles on real pointer events, not programmatic
    // button clicks — dispatch Escape so its built-in close handler runs.
    const handleCloseListbox = useCallback(() => {
      listboxOptionsRef.current?.dispatchEvent(
        new KeyboardEvent("keydown", {
          key: "Escape",
          code: "Escape",
          bubbles: true,
          cancelable: true,
        })
      );
      reset();
    }, [reset]);

    const phoneInput = useMemo(
      () => (
        <input
          value={value.phone}
          onChange={handlePhoneChange}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder={placeholder}
          onKeyDown={(e) => {
            if (!blockNotAllowedCharacters) {
              return;
            }
            const allowedCharacters = [
              "1",
              "2",
              "3",
              "4",
              "5",
              "6",
              "7",
              "8",
              "9",
              "0",
              "Backspace",
              "Delete",
              "ArrowUp",
              "ArrowDown",
              "ArrowLeft",
              "ArrowRight",
              "Tab",
              "Enter",
            ];
            if (!allowedCharacters.includes(e.key)) {
              e.preventDefault();
            }
          }}
          className={cx(
            "flex-1 rounded-[10px] border-none py-3 text-base font-medium focus:outline-none",
            inputClassName
          )}
          ref={ref}
          maxLength={maxLength}
        />
      ),
      [
        blockNotAllowedCharacters,
        handlePhoneChange,
        inputClassName,
        maxLength,
        onBlur,
        onFocus,
        placeholder,
        ref,
        value.phone,
      ]
    );

    const vListWithSearchInput = useMemo(
      () => (
        <>
          <SearchInput
            variant="filled"
            color="grey"
            ref={inputRef}
            {...inputProps}
            onReset={reset}
            onClear={reset}
            onKeyDown={(e) => {
              if (e.key === " ") {
                e.stopPropagation();
              }
            }}
          />
          {searchedCountries.length > 0 ? (
            <VList
              style={{
                flexGrow: "1",
                width: "100%",
              }}
              overscan={1000}
            >
              {searchedCountries.map(({ name, code, iso, flag }, index) => (
                <Fragment key={`${iso}-${code}`}>
                  <ListboxOption
                    value={`${iso}-${code}`}
                    className={({ active }) =>
                      cx(
                        `relative mb-2 cursor-pointer rounded-lg select-none ${
                          active && "bg-primary-50"
                        }`,
                        {
                          "bg-primary-50":
                            `${iso}-${code}` === `${value.iso}-${value.code}`,
                          "mt-4": index === 0,
                        }
                      )
                    }
                  >
                    <div className="flex items-center justify-between px-4 py-3 font-secondary text-dark">
                      <div className="flex items-center">
                        <img
                          src={flag}
                          alt={name}
                          className="mr-[10px] w-[28px]"
                        />
                        <span>
                          {name} ({iso})
                        </span>
                      </div>
                      <span>{code}</span>
                    </div>
                  </ListboxOption>
                  {index === 2 && !searchQuery && (
                    <div className="my-4 h-px w-full bg-dark-100" />
                  )}
                </Fragment>
              ))}
            </VList>
          ) : (
            <div className="mt-2 w-full text-center text-sm leading-[21px] text-[#4F4F4F]">
              {t({
                id: "list-box-component.no-results",
                defaultMessage: "No results 😔",
              })}
            </div>
          )}
        </>
      ),
      [
        inputProps,
        reset,
        searchQuery,
        searchedCountries,
        t,
        value.code,
        value.iso,
      ]
    );

    if (isMobile) {
      return (
        <div
          className={cx("relative flex items-center gap-2", wrapperClassName)}
        >
          <PlainButton
            className={cx(
              "flex h-[57px] cursor-pointer items-center border-0 bg-transparent py-4 pl-4 font-secondary text-base font-medium",
              listButtonClassName
            )}
            onClick={() => setIsMobileDrawerOpen(true)}
            ref={assignRefs([refs.setReference, listboxButtonRef])}
          >
            {value.code} <ArrowIcon className={classNames("ml-3")} />
          </PlainButton>
          <MobileDrawer
            size="xl"
            isOpen={isMobileDrawerOpen}
            onClose={() => setIsMobileDrawerOpen(false)}
            header={{
              title: t({
                id: "phone-input-component.select-country",
                defaultMessage: "Select Country",
              }),
            }}
          >
            <MobileDrawer.Content>
              <Listbox onChange={handleCountrySelect} value={undefined}>
                {vListWithSearchInput}
              </Listbox>
            </MobileDrawer.Content>
          </MobileDrawer>
          {!hideVerticalLine && <div className="h-[30px] w-px bg-grey-300" />}
          {phoneInput}
        </div>
      );
    }
    return (
      <div className={cx("relative flex items-center gap-2", wrapperClassName)}>
        <Listbox onChange={handleCountrySelect} value={undefined}>
          <ListboxButton
            className={cx(
              "flex cursor-pointer items-center border-0 bg-transparent py-4 pl-4 font-secondary text-base font-medium",
              listButtonClassName
            )}
            onClick={handleSelect}
            ref={assignRefs([refs.setReference, listboxButtonRef])}
          >
            {value.code} <ArrowIcon className={classNames("ml-3")} />
          </ListboxButton>
          {/* TODO: check position and height of list  */}
          <Portal>
            <ListboxOptions
              autoFocus={false}
              className={cx(
                "z-9999 flex h-[450px] w-[375px] flex-col overflow-x-auto rounded-2xl bg-white px-4 pt-6 shadow-[0px_4px_16px_rgba(0,0,0,0.16)] focus:outline-none",
                className
              )}
              ref={assignRefs([refs.setFloating, listboxOptionsRef])}
              style={{ ...floatingStyles }}
            >
              <div className="flex items-center justify-between">
                <span className="font-secondary font-semibold text-[#383838]">
                  {t({
                    id: "phone-input-component.select-country",
                    defaultMessage: "Select Country",
                  })}
                </span>
                <PlainButton onClick={handleCloseListbox}>
                  <CloseIcon />
                </PlainButton>
              </div>
              <div className="my-4 h-px w-full bg-dark-100" />
              {vListWithSearchInput}
            </ListboxOptions>
          </Portal>
        </Listbox>
        {!hideVerticalLine && <div className="h-[30px] w-px bg-grey-300" />}
        {phoneInput}
      </div>
    );
  }
);

export default PhoneInput;
